import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Badge, Spinner, 
  Alert, Modal, Form, InputGroup 
} from 'react-bootstrap';
import { 
  FaEdit, FaTrash, FaPlus, FaSearch, 
  FaUser, FaEnvelope, FaPhone 
} from 'react-icons/fa';
import axios from 'axios';
import { Link } from 'react-router-dom';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchUsers = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/users`, {
        headers: { 'x-auth-token': token },
        params: { 
          page,
          search: searchTerm 
        }
      });
      
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchTerm]);

  const handleDelete = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/users/${userToDelete._id}`, {
        headers: { 'x-auth-token': token }
      });
      
      setShowDeleteModal(false);
      fetchUsers(currentPage);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const getAuthMethodBadge = (method) => {
    const variants = {
      email: 'primary',
      google: 'danger',
      facebook: 'info'
    };
    return <Badge bg={variants[method] || 'secondary'}>{method}</Badge>;
  };

  return (
    <div className="users-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>User Management</h2>
        <Link to="/admin/users/add" className="btn btn-primary">
          <FaPlus className="me-2" />
          Add New User
        </Link>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search users by name, email or phone"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>User</th>
                <th>Contact</th>
                <th>Auth Method</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <FaUser className="me-2" />
                        <div>
                          <div>{user.name}</div>
                          <small className="text-muted">{user.email}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div><FaPhone className="me-2" /> {user.phone || 'N/A'}</div>
                        <div><FaEnvelope className="me-2" /> {user.email}</div>
                      </div>
                    </td>
                    <td>{getAuthMethodBadge(user.authMethod)}</td>
                    <td>
                      <Badge bg={user.usertype === 'admin' ? 'warning' : 'success'}>
                        {user.usertype}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Link 
                          to={`/admin/users/edit/${user._id}`}
                          className="btn btn-sm btn-primary"
                        >
                          <FaEdit />
                        </Link>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setUserToDelete(user);
                            setShowDeleteModal(true);
                          }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => fetchUsers(currentPage - 1)}
                    >
                      Previous
                    </button>
                  </li>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <li 
                      key={page} 
                      className={`page-item ${currentPage === page ? 'active' : ''}`}
                    >
                      <button 
                        className="page-link" 
                        onClick={() => fetchUsers(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                  
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => fetchUsers(currentPage + 1)}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete user <strong>{userToDelete?.name}</strong>? 
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete User
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserList;